import { message } from 'antd';
import Image from 'next/image';
import { useCallback, useEffect, useRef, useState } from 'react';

import Button from '@/components/common/Button';
import Loading from '@/components/common/Loading';
import { usePartial } from '@/hooks/usePartial';
import { isCnArea } from '@/utils/envHelper';
import { t } from '@/utils/helper/intl';

import CommonModal from '../index';
import { textComplianceReview, updateAsset } from '../service';
import { ScriptUploadContent } from '../upload/components/ScriptUploadPanel';
import { player } from '../utils/playerClass';
import ControllerBar from './components/ControllerBar';

interface PreviewModal {
  title: string;
  type: 'video' | 'script' | 'audio' | 'image';
  fileUrl: string;
  id?: number;
  onClose?: () => void;
}
const ImageContent = (props: PreviewModal) => {
  return (
    <div>
      <div className="w-full h-[506px]">
        <div className="flex flex-col h-full justify-center items-center relative bg-common-modal-upload-popover-bg-color rounded-[6px] overflow-hidden p-[10px]">
          <Image
            className="h-full w-auto"
            src={props.fileUrl}
            alt=""
            width={500}
            height={500}
          />
          <div
            className="absolute bottom-0  w-full z-10 h-[40px]"
            style={{
              background:
                'linear-gradient(0deg, #000 -11.3%, rgba(0, 0, 0, 0.38) 37.13%, rgba(0, 0, 0, 0.00) 96.56%)',
            }}
          />
        </div>
      </div>
      <div className="w-full pb-[6px] pt-[22px] flex flex-row items-center justify-center">
        <Button onClick={props.onClose} className="!font-normal !px-[48px] ">
          {t('merror.operate.ok')}
        </Button>
      </div>
    </div>
  );
};

export const VideoPreviewContent = ({ url }: { url: string }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (videoRef.current) {
      player.setMediaElement(videoRef.current);

      // 添加加载完成的监听
      const handleCanPlay = () => {
        setIsLoading(false);
      };

      videoRef.current.addEventListener('canplay', handleCanPlay);

      return () => {
        videoRef.current?.removeEventListener('canplay', handleCanPlay);
        player.destroy();
      };
    }
  }, []);
  return (
    <div className="flex flex-col h-full justify-center items-center relative bg-common-modal-upload-popover-bg-color rounded-[6px] overflow-hidden p-[10px]">
      {isLoading && <Loading />}
      <video
        ref={videoRef}
        className="h-full w-auto max-h-full max-w-full rounded-[8px]"
        controls={false}
      />
      <div
        className="absolute bottom-0  w-full z-10 h-[40px]"
        style={{
          background:
            'linear-gradient(0deg, #000 -11.3%, rgba(0, 0, 0, 0.38) 37.13%, rgba(0, 0, 0, 0.00) 96.56%)',
        }}
      ></div>
      <div className="absolute w-full z-20  bottom-0 px-[12px]">
        <div className="w-full">
          <ControllerBar url={url} videoRef={videoRef} />
        </div>
      </div>
    </div>
  );
};
const VideoContent = (props: PreviewModal) => {
  return (
    <div>
      <div className="w-full h-[506px]">
        <VideoPreviewContent url={props.fileUrl} />
      </div>
      <div className="w-full pb-[6px] pt-[22px] flex flex-row items-center justify-center">
        <Button onClick={props.onClose} className="!font-normal !px-[48px] ">
          {t('merror.operate.ok')}
        </Button>
      </div>
    </div>
  );
};
const ScriptContent = (props: PreviewModal) => {
  const [inputData, setInputData] = usePartial({
    title: props.title || '',
    content: props.fileUrl || '',
  });

  const onSave = async (type: 'add' | 'update' | null, isJump?: boolean) => {
    if (!inputData.title) {
      message.warning(t('merror.tip.pleaseInputTitle'));
      return;
    }
    if (!inputData.content) {
      message.warning(t('merror.tip.pleaseInputContent'));
      return;
    }
    if (isCnArea) {
      try {
        const result = await textComplianceReview(inputData.content);
        if (result.success && result?.data?.reviewState !== 1) {
          message.error(t('The text content is not compliant'));
          return;
        }
      } catch (error) {
        message.error(t('Text content compliance check failed'));
        console.error('文件合规检查catch', error);
        return;
      }
    }

    const params = {
      id: props.id!,
      title: inputData.title,
      text: inputData.content,
    };
    try {
      const res = await updateAsset(params);
      if (res.success) {
        if (isJump) {
          localStorage.setItem(
            'avatarScriptInfo',
            JSON.stringify({
              title: inputData.title,
              fileUrl: inputData.content,
            }),
          );
          // 跳转到数字人视频
          localStorage.removeItem('projectUUID');
          window.location.href = window.location.origin + '/lip-sync';
        } else {
          message.success(t('merror.tip.uploadSuccess'));
          props.onClose?.();
        }
      } else {
        message.error(t('merror.tip.uploadFailed'));
      }
    } catch {
      message.error(t('merror.tip.uploadFailed'));
    }
  };
  const onChangeContent = useCallback((newContent: string) => {
    setInputData({ content: newContent });
  }, []);
  const onChangeTitle = useCallback((newTitle: string) => {
    setInputData({ title: newTitle });
  }, []);
  return (
    <ScriptUploadContent
      type="update"
      title={inputData.title}
      content={inputData.content}
      loading={false}
      submitLoading={false}
      onChangeContent={onChangeContent}
      onChangeTitle={onChangeTitle}
      onSave={onSave}
    />
  );
};

const AudioContent = (props: PreviewModal) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    if (audioRef.current) {
      player.setMediaElement(audioRef.current);

      // 添加加载完成的监听
      const handleCanPlay = () => {
        setIsLoading(false);
      };

      audioRef.current.addEventListener('canplay', handleCanPlay);

      return () => {
        audioRef.current?.removeEventListener('canplay', handleCanPlay);
        player.destroy();
      };
    }
  }, []);
  return (
    <div className="flex flex-col h-[80px] justify-center items-center relative rounded-[6px] overflow-hidden ">
      {isLoading && <Loading />}
      <audio
        ref={audioRef}
        className="h-full w-auto max-h-full max-w-full rounded-[8px] hidden"
        controls={false}
      />

      <div className="w-full px-[50px]">
        <div className=" flex justify-center">
          <ControllerBar url={props.fileUrl} className="flex-1" />
        </div>
      </div>
    </div>
  );
};

const Content = (props: PreviewModal) => {
  if (props.type === 'image') {
    return <ImageContent {...props} />;
  }
  if (props.type === 'video') {
    return <VideoContent {...props} />;
  }
  if (props.type === 'script') {
    return <ScriptContent {...props} />;
  }
  if (props.type === 'audio') {
    return <AudioContent {...props} />;
  }
};
export function previewModalShow(options: PreviewModal) {
  const width = options.type === 'video' ? 1200 : 690;
  CommonModal.show((t) => ({
    width,
    title: options.title,
    footer: false,
    content: (close) => (
      <Content
        {...options}
        onClose={() => {
          close();
          options.onClose?.();
        }}
      />
    ),
  }));
}
